import { useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";
import ChevronLeftIcon from "@heroicons/react/24/outline/ChevronLeftIcon";
import ChevronRightIcon from "@heroicons/react/24/outline/ChevronRightIcon";
import ArrowDownTrayIcon from "@heroicons/react/24/outline/ArrowDownTrayIcon";
import { ExistingCraftRecord } from "../../models/craft-record";
import { Timestamp } from "firebase/firestore";
import { convertToReadableTimestampDate } from "../../assets/js/convertToReadableTimestamp";
import { DbRead } from "../../database";
import { createToastMessageID } from "../../utils";
import { useToastMessageStore } from "../../store/toast-messages";
import { logger } from "../../logging";
import LoadingSpinner from "../../components/LoadingSpinner";

export interface PhotoForGallery {
  id: string;
  photoURL: string;
  photoURLReduced: string;
  photoURLThumbnail: string;
  timestampCreated: Timestamp;
  description: string;
  craftRecordID: string;
}

interface ProjectPhotoGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  photosByCraftRecord: Record<string, PhotoForGallery[]>;
  craftRecords: ExistingCraftRecord[];
  projectID: string;
  siteKey: string;
}

export default function ProjectPhotoGallery({
  isOpen,
  onClose,
  photosByCraftRecord,
  craftRecords,
  projectID,
  siteKey,
}: ProjectPhotoGalleryProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoForGallery | null>(
    null,
  );
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [downloadingJobs, setDownloadingJobs] = useState<
    Record<string, boolean>
  >({});
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );

  // Flatten all photos for navigation in the lightbox
  const allPhotos = Object.values(photosByCraftRecord).flat();

  // Get current photo index for navigation
  const currentPhotoIndex = selectedPhoto
    ? allPhotos.findIndex((photo) => photo.id === selectedPhoto.id)
    : -1;

  const handlePhotoClick = (photo: PhotoForGallery, jobId: string) => {
    setSelectedPhoto(photo);
    setCurrentJobId(jobId);
  };

  const handlePrevPhoto = () => {
    if (currentPhotoIndex > 0) {
      setSelectedPhoto(allPhotos[currentPhotoIndex - 1]);
      // Find the job ID for this photo
      for (const [jobId, photos] of Object.entries(photosByCraftRecord)) {
        if (
          photos.some(
            (photo) => photo.id === allPhotos[currentPhotoIndex - 1].id,
          )
        ) {
          setCurrentJobId(jobId);
          break;
        }
      }
    }
  };

  const handleNextPhoto = () => {
    if (currentPhotoIndex < allPhotos.length - 1) {
      setSelectedPhoto(allPhotos[currentPhotoIndex + 1]);
      // Find the job ID for this photo
      for (const [jobId, photos] of Object.entries(photosByCraftRecord)) {
        if (
          photos.some(
            (photo) => photo.id === allPhotos[currentPhotoIndex + 1].id,
          )
        ) {
          setCurrentJobId(jobId);
          break;
        }
      }
    }
  };

  const closeLightbox = () => {
    setSelectedPhoto(null);
  };

  // Find job name by ID
  const getJobName = (jobId: string) => {
    const job = craftRecords.find((cr) => cr.id === jobId);
    return job?.title || "Unknown Job";
  };

  // Handle downloading photos for a specific job
  const handleDownloadJobPhotos = async (
    jobId: string,
    showSuccessToast = true,
  ): Promise<void> => {
    if (!siteKey) return;

    try {
      setDownloadingJobs((prev) => ({ ...prev, [jobId]: true }));

      const url = await DbRead.parentRecordPhotos.downloadAll({
        siteKey,
        workRecordID: jobId,
      });

      // Get the job title for the filename
      const jobTitle = getJobName(jobId);

      // Fetch the zip file data
      const response = await fetch(url);
      const blob = await response.blob();

      // Create a new URL for the blob with our custom filename
      const blobUrl = window.URL.createObjectURL(blob);

      // Create anchor element and dispatch a click to trigger download
      const anch = document.createElement("a");
      anch.download = `${jobTitle}.zip`;
      anch.href = blobUrl;
      document.body.appendChild(anch);
      anch.click();

      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
        anch.remove();
      }, 100);

      if (showSuccessToast) {
        addToastMessage({
          id: createToastMessageID(),
          message: `Photos for ${jobTitle} downloaded successfully`,
          dialog: false,
          type: "success",
        });
      }
    } catch (e) {
      logger.error(`Error downloading photos for job ${jobId}:`, e);
      addToastMessage({
        id: createToastMessageID(),
        message: `Failed to download photos for ${getJobName(jobId)}`,
        dialog: false,
        type: "error",
      });
      throw e; // Re-throw to allow catching in the Export All function
    } finally {
      setDownloadingJobs((prev) => ({ ...prev, [jobId]: false }));
    }
  };

  return (
    <>
      {/* Main Gallery Dialog */}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-xl bg-white p-6 shadow-xl transition-all">
                  <div className="mb-4 flex items-center justify-between">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold leading-6 text-gray-900"
                    >
                      Project Photos
                    </Dialog.Title>
                    <div className="flex items-center gap-2">
                      {Object.keys(photosByCraftRecord).length > 0 && (
                        <button
                          type="button"
                          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          onClick={async () => {
                            const jobs = Object.keys(photosByCraftRecord);

                            // Set all jobs to downloading state
                            const newDownloadingState = jobs.reduce(
                              (acc, jobId) => {
                                acc[jobId] = true;
                                return acc;
                              },
                              {} as Record<string, boolean>,
                            );
                            setDownloadingJobs(newDownloadingState);

                            try {
                              // Process each job sequentially to avoid browser throttling
                              for (const jobId of jobs) {
                                const jobTitle = getJobName(jobId);

                                try {
                                  addToastMessage({
                                    id: createToastMessageID(),
                                    message: `Downloading photos for ${jobTitle}...`,
                                    dialog: false,
                                    type: "info",
                                  });

                                  // Wait for each download to complete before starting the next
                                  await new Promise<void>((resolve) => {
                                    handleDownloadJobPhotos(jobId, false)
                                      .then(() => resolve())
                                      .catch(() => {
                                        // Resolve anyway to continue with next job
                                        resolve();
                                      });
                                  });

                                  // Small delay between downloads
                                  await new Promise((r) => setTimeout(r, 500));
                                } catch (error) {
                                  // Continue with next job even if one fails
                                  logger.error(
                                    `Error in export all loop for job ${jobId}:`,
                                    error,
                                  );
                                }
                              }

                              addToastMessage({
                                id: createToastMessageID(),
                                message:
                                  "All photo exports have been initiated",
                                dialog: false,
                                type: "success",
                              });
                            } catch (e) {
                              logger.error("Export all photos error:", e);
                              addToastMessage({
                                id: createToastMessageID(),
                                message: "Error exporting photos",
                                dialog: false,
                                type: "error",
                              });
                            } finally {
                              // Reset all downloading states
                              setDownloadingJobs({});
                            }
                          }}
                        >
                          <ArrowDownTrayIcon
                            className="mr-2 h-4 w-4"
                            aria-hidden="true"
                          />
                          Export All Photos
                        </button>
                      )}
                      <button
                        type="button"
                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onClick={onClose}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 max-h-[70vh] overflow-y-auto">
                    {Object.entries(photosByCraftRecord).map(
                      ([jobId, photos]) =>
                        photos.length > 0 && (
                          <div key={jobId} className="mb-6">
                            <div className="mb-2 flex items-center justify-between">
                              <h4 className="font-medium text-gray-800">
                                {getJobName(jobId)}
                              </h4>
                              <button
                                type="button"
                                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                onClick={() => handleDownloadJobPhotos(jobId)}
                                disabled={downloadingJobs[jobId]}
                              >
                                {downloadingJobs[jobId] ? (
                                  <LoadingSpinner
                                    marginClass="mr-2"
                                    colorClass="text-blue-500"
                                    sizeClass="h-4 w-4"
                                  />
                                ) : (
                                  <ArrowDownTrayIcon
                                    className="mr-2 h-4 w-4"
                                    aria-hidden="true"
                                  />
                                )}
                                {downloadingJobs[jobId]
                                  ? "Downloading..."
                                  : "Download Photos"}
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                              {photos.map((photo) => (
                                <div
                                  key={photo.id}
                                  className="relative aspect-square cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-gray-100 transition-opacity hover:opacity-90"
                                  onClick={() => handlePhotoClick(photo, jobId)}
                                >
                                  <img
                                    src={photo.photoURL}
                                    alt={photo.description || "Project photo"}
                                    className="h-full w-full object-cover"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        ),
                    )}

                    {Object.values(photosByCraftRecord).flat().length === 0 && (
                      <div className="py-8 text-center text-gray-500">
                        No photos available for this project.
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Photo Lightbox */}
      <Transition appear show={!!selectedPhoto} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeLightbox}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-90" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden transition-all">
                  {selectedPhoto && (
                    <div className="relative">
                      {/* Navigation buttons */}
                      <div className="absolute left-4 top-1/2 z-10 -translate-y-1/2 transform">
                        <button
                          onClick={handlePrevPhoto}
                          disabled={currentPhotoIndex <= 0}
                          className="rounded-full bg-black bg-opacity-50 p-2 text-white hover:bg-opacity-70 focus:outline-none disabled:opacity-30"
                        >
                          <ChevronLeftIcon className="h-6 w-6" />
                        </button>
                      </div>

                      <div className="absolute right-4 top-1/2 z-10 -translate-y-1/2 transform">
                        <button
                          onClick={handleNextPhoto}
                          disabled={currentPhotoIndex >= allPhotos.length - 1}
                          className="rounded-full bg-black bg-opacity-50 p-2 text-white hover:bg-opacity-70 focus:outline-none disabled:opacity-30"
                        >
                          <ChevronRightIcon className="h-6 w-6" />
                        </button>
                      </div>

                      {/* Close button */}
                      <button
                        onClick={closeLightbox}
                        className="absolute right-4 top-4 z-10 rounded-full bg-black bg-opacity-50 p-2 text-white hover:bg-opacity-70 focus:outline-none"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>

                      {/* Main photo */}
                      <div className="flex justify-center">
                        <img
                          src={selectedPhoto.photoURL}
                          alt={selectedPhoto.description || "Project photo"}
                          className="max-h-[70vh] object-contain"
                        />
                      </div>

                      {/* Photo info */}
                      <div className="mt-2 rounded-lg bg-white p-4">
                        <div className="mb-1 text-sm text-gray-500">
                          Job:{" "}
                          <span className="font-medium text-gray-800">
                            {currentJobId
                              ? getJobName(currentJobId)
                              : "Unknown"}
                          </span>
                        </div>
                        {selectedPhoto.description && (
                          <p className="mb-1 text-gray-800">
                            {selectedPhoto.description}
                          </p>
                        )}
                        <div className="text-sm text-gray-500">
                          Taken:{" "}
                          <span className="font-medium text-gray-800">
                            {selectedPhoto.timestampCreated
                              ? convertToReadableTimestampDate(
                                  selectedPhoto.timestampCreated.toMillis(),
                                )
                              : "Unknown date"}
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-gray-500">
                          {currentPhotoIndex + 1} of {allPhotos.length}
                        </div>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
